import clsx from "clsx";
import { ReactNode } from "react";

interface FieldRowProps {
  dataTestid?: string;
  className?: string;
  label: ReactNode;
  icon?: ReactNode;
  value?: ReactNode;
  /** Wraps a long value (e.g. a 2FA key) onto further lines instead of truncating it. */
  wrapValue?: boolean;
  disabled?: boolean;
}

export const FieldRow = ({
  dataTestid,
  className,
  label,
  icon,
  value,
  wrapValue = false,
  disabled = false,
}: FieldRowProps) => {
  const isEmpty = value === undefined || value === null || value === "";
  const textColor = disabled
    ? "text-foreground-tertiary"
    : "text-foreground-primary";

  return (
    <div
      data-testid={dataTestid}
      className={clsx(
        // pl-[18px], py-3.5 and border-2 (transparent) match the editable
        // stacked FormField's own box model (FormField.scss's
        // `.form-field-container--stacked`) exactly - that rule's min-h-14
        // floor doesn't actually cap real 2-line label+value content at
        // 64px once its padding and reserved border are added, it settles
        // ~4px taller. pr-5 matches where the editable field's absolutely
        // positioned icon sits (`right-5` inside the same border). Matching
        // the same recipe keeps this read-only row the same size, text and
        // icon position as its editable counterpart instead of a jump on
        // toggle.
        "flex min-h-14 min-w-0 items-center justify-between gap-3 rounded border-2 border-transparent bg-surface-tertiary py-3.5 pl-[18px] pr-5",
        disabled && "bg-surface-quaternary",
        textColor,
        className,
      )}
    >
      {isEmpty ? (
        // Empty state: only the label, at value size, vertically centered
        <span className="truncate text-base font-semibold leading-md">
          {label}
        </span>
      ) : (
        <div className="flex min-w-0 flex-col">
          <span className="text-xs font-semibold leading-md">{label}</span>
          <span
            className={clsx(
              "text-base font-semibold leading-md",
              wrapValue ? "break-all" : "truncate",
            )}
            title={
              typeof value === "string" || typeof value === "number"
                ? String(value)
                : undefined
            }
          >
            {value}
          </span>
        </div>
      )}
      {icon}
    </div>
  );
};
